import * as React from 'react'
import { cn } from '@/lib/utils'

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /**
   * The band's left side — what this table is a list of, usually its count.
   *
   * Deliberately not the page's own title, which is already on screen an inch
   * above. The count is the thing a list header can say that the page cannot.
   */
  heading?: React.ReactNode
  /**
   * The band's right side: the list's filters and the action that adds to it.
   *
   * Plan View puts a list page's main action here rather than loose on the page
   * above: a primary `sm` button when it is the one main action in the view
   * ("New event"), a ghost one when the page already has its own ("Invite" in a
   * settings section). Filters come first, the button last; on a narrow screen
   * the band wraps and the controls drop under the count.
   */
  actions?: React.ReactNode
  /**
   * The bar under the list — `<Pagination>`, in practice.
   *
   * Inside the panel's border, because a list's paging belongs to the list for
   * the same reason its "New" button does. `Pagination` renders nothing when
   * there is one page, so a table can always pass it and a short list stays
   * plain.
   */
  footer?: React.ReactNode
}

/**
 * Data table on Plan View's recipe (design-system `.ds-table`): a white panel
 * with the surface corner, column headers in sentence case on a stone band —
 * like a drawing's title block — and rows separated by hairlines. Compose with
 * the parts below.
 *
 * `TableHeader` owns the single header `<tr>` — pass `TableHead` cells as its
 * direct children (do not wrap them in `TableRow`).
 *
 * `heading`/`actions` render the panel header band (`.ds-panel__header`): the
 * design system attaches a list's own controls to the top of the list, inside
 * its border, rather than leaving them loose on the page above it. That is the
 * difference between a button that belongs to this table and a button that
 * happens to sit near it.
 */
export function Table({ heading, actions, footer, className, ...props }: TableProps) {
  const band = heading != null || actions != null
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      {band ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3">
          <span className="text-sm font-semibold text-ink">{heading}</span>
          {actions != null ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
        </div>
      ) : null}
      {/* `relative` makes the scroll box the containing block for anything
          absolutely positioned inside the table, so it is clipped and scrolled
          with the rest. Without it, `TableActionsHead`'s `sr-only` label was
          positioned against the page instead, sat at the far end of a wide
          table, and gave a phone a page that scrolled sideways. */}
      <div className="relative overflow-x-auto">
        <table className={cn('w-full border-collapse text-left text-sm', className)} {...props} />
      </div>
      {footer}
    </div>
  )
}

/**
 * The column header row. Pass `TableHead` cells as children only — this wraps
 * them in one `<tr>` on the stone band (design-system `.ds-table th`).
 */
export function TableHeader({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr className="bg-surface-sunken text-xs font-medium tracking-label text-ink-muted">{children}</tr>
    </thead>
  )
}

export function TableHead({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        // The type is also on the cell so it holds even if a parent overrides the
        // header row's text utilities. Sentence case: a column header is a label,
        // not a shout.
        'whitespace-nowrap border-b border-line bg-surface-sunken px-4 py-2.5 text-left text-xs font-medium tracking-label text-ink-muted',
        className,
      )}
      {...props}
    />
  )
}

export function TableBody(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />
}

export function TableRow({
  selected = false,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & {
  /** Matches `.ds-row--selected`: the row this view is currently about. */
  selected?: boolean
}) {
  return (
    <tr
      className={cn(
        // `border-line`, not `border-line`: the design system separates rows
        // with the fainter of its two hairlines and encloses the table with the
        // stronger one. Using the strong one for both made every table read as a
        // grid of boxes rather than a list.
        'group border-t border-line transition-colors',
        // `bg-row-hover` is Plan View's row wash, lighter than a control's hover:
        // a row is a big target, and the same fill on it reads as a selection.
        selected
          ? 'bg-surface-selected hover:bg-surface-selected-hover'
          : 'hover:bg-row-hover',
        className,
      )}
      aria-selected={selected || undefined}
      {...props}
    />
  )
}

/**
 * The header cell over the actions column: announced, not drawn.
 *
 * The label is deliberately not shown. "ACTIONS" over a column of icons names
 * the obvious, and being the widest thing in that column it sets the column's
 * width — so the word pushes the data around. Worse, the actions themselves
 * only appear on approach, which left a labeled column with nothing under it
 * on every table in the app; it read as unfinished rather than as quiet.
 *
 * It is a visually hidden name rather than an empty cell, so a screen reader
 * still announces which column it is in.
 */
export function TableActionsHead({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <TableHead className={cn('w-[1%] whitespace-nowrap', className)} {...props}>
      <span className="sr-only">Actions</span>
    </TableHead>
  )
}

/**
 * The cell that holds a row's `<RowActions>`.
 *
 * `w-[1%]` with `whitespace-nowrap` is the old trick for "as narrow as its
 * content" in an auto-laid-out table: the column takes what the controls need
 * and gives the rest back to the data.
 */
export function TableActionsCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <TableCell className={cn('w-[1%] whitespace-nowrap text-right', className)} {...props} />
}

/**
 * The "nothing here yet" row.
 *
 * A table with a header band keeps its band when the list is empty, so the
 * action that would fill it stays where it was — replacing the whole table with
 * a paragraph took the "Invite" button off screen at exactly the moment
 * somebody needed it.
 */
export function TableEmptyRow({
  colSpan,
  children,
}: {
  colSpan: number
  children: React.ReactNode
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-ink-muted">
        {children}
      </td>
    </tr>
  )
}

export function TableCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('px-4 py-2.5 align-middle', className)} {...props} />
}
